import { AnyAction, AsyncThunk, createSlice, createAsyncThunk } from '@reduxjs/toolkit';
// eslint-disable-next-line
import parsePhoneNumber from 'libphonenumber-js';
import {
  ChallengeIdReqType,
  CorrelationidType,
  ErrorHandlers,
  ForgotAmwayIdReqPayload,
  ForgotPasswordComponentKeyEnum,
  GenotpErrorResponseHandler,
  Identity,
  IdentityResponse,
  InitialIdentityState,
  ProfileIdReqType,
} from '../../types/identityTypes';

import {
  ChallengeIdPatchRequest,
  OtpGeneration,
  OtpResend,
  RequestIdGenerated,
  VerifyOtp,
  WorkflowResponse,
} from '../models';

import { identityApi } from '../requests';
import { UpdatePasswordWithWorkflowRequest } from '../models/UpdatePasswordWithWorkflowRequest';
import { AuthenticateRequest } from '../models/AuthenticateRequest';
import { UpdatePasswordRequestWithChallengeId } from '../models/UpdatePasswordRequestWithChallengeId';
import { generateChallengeIdForUpdatePasswordThunk, generateWorkflowId } from './thunks';

const HYDRATE = '__NEXT_REDUX_WRAPPER_HYDRATE__';

type IdentityExtraType = {
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  dispatch: any;
  state: InitialIdentityState;
  extra: {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    BASE_API_URL: string;
  };
};

type GenericAsyncThunk = AsyncThunk<IdentityResponse, unknown, IdentityExtraType>;
type PendingAction = ReturnType<GenericAsyncThunk['pending']>;
type RejectedAction = ReturnType<GenericAsyncThunk['rejected']>;
type FulfilledAction = ReturnType<GenericAsyncThunk['fulfilled']>;

function isPendingGenericAction(action: AnyAction): action is PendingAction {
  return action.type.endsWith('/pending');
}
function isFulfilledAction(action: AnyAction): action is FulfilledAction {
  return action.type.endsWith('/fulfilled');
}

function isRejectedAction(action: AnyAction): action is RejectedAction {
  return action.type.endsWith('/rejected');
}

const forgotPasswordInitialData = {
  isNextEnable: false,
  hasError: false,
  isLoading: false,
  currentPage: ForgotPasswordComponentKeyEnum.USER_INFO_FORM,
  username: '',
  email: '',
  countryCode: '',
  phone: '',
  workflowId: '',
  challengeId: '',
  showHelpModal: false,
};

const loginPageInitialData = {
  shouldLoginButtonBeEnable: false,
};

const initialState: InitialIdentityState = {
  error: {},
  hasError: false,
  isLoading: false,
  updatePasswordData: {
    hasError: false,
    isLoading: false,
    enableChangePasswordBtn: false,
    isPasswordChanged: false,
    currentPassword: '',
    confirmPassword: '',
    createPassword: '',
  },
  forgotPassword: {
    isOtpSent: false,
    isOtpVerified: false,
    currentPage: 'ForgotPasswordForm',
    showHelpModal: false,
    showNeedMoreHelp: false,
    showDivider: true,
    showHocFooterBtn: true,
    showBackToSignin: true,
  },
  forgotUsername: {
    isUsernameSent: false,
    showHelpModal: false,
  },
  usernameData: {
    isNextEnable: false,
    hasError: false,
  },
  sendOtpData: {
    isNextEnable: false,
    hasError: false,
  },
  otpData: {
    requestId: '',
    challengeId: '',
    username: '',
    isOtpSent: false,
    isVerified: false,
    isChecking: false,
    isInvalid: false,
    otp: {
      input0: '',
      input1: '',
      input2: '',
      input3: '',
      input4: '',
      input5: '',
    },
  },
  userData: {
    countryCode: '',
    email: '',
    phone: '',
    username: '',
  },
  resendClockData: {
    threshold: 60,
    secondsLeft: 60,
  },
  socialAccountDelinkPageData: {
    isLoading: false,
    hasError: false,
  },
  forgotPasswordPageData: forgotPasswordInitialData,
  loginPageData: loginPageInitialData,
  otpRequestCount: 0,
};

export const generateTokenIdentitySliceReq = createAsyncThunk<
  IdentityResponse,
  AuthenticateRequest & ErrorHandlers,
  {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    dispatch: any;
    extra: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      BASE_API_URL: string;
    };
    state: InitialIdentityState;
  }
>('commerceIdentity/generateTokenIdentitySliceReq', async ({ username, password, jansKey, errorHandlers }) => {
  const res = await identityApi.generateToken({
    username,
    password,
    jansKey,
    errorHandlers,
  });
  return res;
});

export const getUserByProfileIdIdentitySliceReq = createAsyncThunk<
  IdentityResponse,
  ProfileIdReqType,
  {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    dispatch: any;
    extra: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      BASE_API_URL: string;
    };
    state: InitialIdentityState;
  }
>('commerceIdentity/getUserByProfileIdIdentitySliceReq', async ({ profileId }, { extra }) => {
  const res = await identityApi.getUserByProfileId({
    profileId,
  });
  return res;
});

export const updatePasswordIdentitySliceReq = createAsyncThunk<
  IdentityResponse,
  UpdatePasswordRequestWithChallengeId & ErrorHandlers & ProfileIdReqType,
  {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    dispatch: any;
    extra: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      BASE_API_URL: string;
    };
    state: InitialIdentityState;
  }
>(
  'commerceIdentity/updatePasswordIdentitySliceReq',
  async ({ newPassword, currentPassword, errorHandlers, challengeId, profileId }) => {
    const res = await identityApi.updatePassword({
      newPassword,
      currentPassword,
      challengeId,
      profileId,
      errorHandlers,
    });

    return res;
  },
);

export const parseCountryCode = (phoneNumberStr: string) => {
  try {
    const obj = parsePhoneNumber(phoneNumberStr);
    return { countryCode: obj?.countryCallingCode, phone: obj?.number };
  } catch (e) {
    return {
      countryCode: '',
      phone: '',
    };
  }
};

export const generateOtpIdentitySliceReq = createAsyncThunk<
  IdentityResponse,
  OtpGeneration & CorrelationidType & GenotpErrorResponseHandler,
  {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    dispatch: any;
    extra: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      BASE_API_URL: string;
    };
    state: InitialIdentityState;
  }
>('commerceIdentity/generateOtp', async ({ otpModes, errorHandlers, otpLength, correlationid }, { dispatch }) => {
  const [modeDetails] = otpModes;
  const { modeValue, modeType } = modeDetails;
  const { countryCode, phone } = parseCountryCode(`${modeValue}`);
  let email = '';
  const username = modeValue;
  if (modeType == 'email') {
    email = `${modeValue}`;
  }
  dispatch(
    updateUserData({
      countryCode,
      email,
      phone,
      username,
    }),
  );
  const res = await identityApi.generateOtp({
    otpModes,
    errorHandlers,
    otpLength,
    correlationid,
  });

  return res;
});

export const resendOtpIdentitySliceReq = createAsyncThunk<
  IdentityResponse,
  OtpResend,
  {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    dispatch: any;
    extra: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      BASE_API_URL: string;
    };
    state: InitialIdentityState;
  }
>('commerceIdentity/resendOtp', async ({ requestId }) => {
  const res = await identityApi.resendOtp({
    requestId,
  });

  return res;
});

export const verifyOtpIdentitySliceReq = createAsyncThunk<
  IdentityResponse,
  VerifyOtp & RequestIdGenerated,
  {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    dispatch: any;
    extra: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      BASE_API_URL: string;
    };
    state: InitialIdentityState;
  }
>('commerceIdentity/verifyOtp', async ({ requestId, otp }) => {
  const res = await identityApi.verifyOtp({
    otp,
    requestId,
  });

  return res;
});

export const forgotAmwayIdIdentitySliceReq = createAsyncThunk<
  IdentityResponse,
  ForgotAmwayIdReqPayload & ErrorHandlers,
  {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    dispatch: any;
    extra: {
      BASE_API_URL: string;
    };
    state: InitialIdentityState;
  }
>('commerceIdentity/forgotAmwayId', async ({ username, errorHandlers }) => {
  const res = await identityApi.forgotAmwayId({
    username,
    errorHandlers,
  });

  return res;
});

export const sendOtpWithChallengeIdSliceReq = createAsyncThunk<
  IdentityResponse,
  ChallengeIdReqType,
  {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    dispatch: any;
    extra: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      BASE_API_URL: string;
    };
    state: InitialIdentityState;
  }
>('commerceIdentity/sendOtpWithChallengeId', async ({ challengeId }) => {
  const res = await identityApi.sendOtpWithChallengeId({
    challengeId,
  });

  return res;
});

export const verifyOtpWithChallengeIdSliceReq = createAsyncThunk<
  IdentityResponse,
  ChallengeIdPatchRequest & ChallengeIdReqType,
  {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    dispatch: any;
    extra: {
      BASE_API_URL: string;
    };
    state: InitialIdentityState;
  }
>('commerceIdentity/verifyOtpWithChallengeId', async ({ challengeId, code }) => {
  const res = await identityApi.verifyOtpWithChallengeId({
    challengeId,
    code,
  });
  return res;
});
export const createPasswordWithWorkflowIdentitySliceReq = createAsyncThunk<
  IdentityResponse,
  UpdatePasswordWithWorkflowRequest & WorkflowResponse & ErrorHandlers,
  {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    dispatch: any;
    extra: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      BASE_API_URL: string;
    };
    state: InitialIdentityState;
  }
>(
  'commerceIdentity/createPasswordWithWorkflowIdentitySliceReq',
  async ({ password, challengeId, workflowId, errorHandlers }) => {
    const res = await identityApi.createPasswordWithWorkflowId({
      password,
      challengeId,
      workflowId,
      errorHandlers,
    });

    return res;
  },
);

export const identitySlice = createSlice({
  extraReducers: (builder) => {
    builder.addCase(HYDRATE, (state: InitialIdentityState) => {
      state.isLoading = true;
    });

    builder.addCase(generateChallengeIdForUpdatePasswordThunk.fulfilled, (state, action) => {
      state.isLoading = false;
      state.hasError = false;
      const { challengeId, modeValue } = action.payload;
      state.otpData.challengeId = challengeId;
      state.otpData.username = modeValue;
    });

    builder
      .addCase(generateWorkflowId.pending, (state) => {
        state.forgotPasswordPageData = {
          ...state.forgotPasswordPageData,
        };
        state.forgotPasswordPageData.isLoading = true;
        state.forgotPasswordPageData.hasError = false;
      })
      .addCase(generateWorkflowId.rejected, (state) => {
        state.forgotPasswordPageData = {
          ...state.forgotPasswordPageData,
        };
        state.forgotPasswordPageData.isLoading = false;
        state.forgotPasswordPageData.hasError = true;
      })
      .addCase(generateWorkflowId.fulfilled, (state, action) => {
        state.forgotPasswordPageData = {
          ...state.forgotPasswordPageData,
        };
        state.forgotPasswordPageData.isLoading = false;
        state.forgotPasswordPageData.hasError = false;
        const { workflowId, challengeId } = action.payload as Identity;
        state.forgotPasswordPageData.workflowId = workflowId;
        state.forgotPasswordPageData.challengeId = challengeId;
      });

    builder
      .addCase(getUserByProfileIdIdentitySliceReq.pending, (state) => {
        state.socialAccountDelinkPageData.isLoading = true;
      })
      .addCase(getUserByProfileIdIdentitySliceReq.rejected, (state) => {
        state.socialAccountDelinkPageData.isLoading = false;
        state.socialAccountDelinkPageData.hasError = true;
      })
      .addCase(getUserByProfileIdIdentitySliceReq.fulfilled, (state, action) => {
        state.socialAccountDelinkPageData.isLoading = false;
        state.socialAccountDelinkPageData.hasError = false;
        const { externalIdentities = [] } = action.payload as Identity;
        state.socialAccountDelinkPageData.socialAccountList = externalIdentities;
      });

    builder.addCase(sendOtpWithChallengeIdSliceReq.fulfilled, (state, action: FulfilledAction) => {
      state.isLoading = false;
      state.hasError = false;
      state.otpData = {
        ...state.otpData,
        isVerified: false,
        isChecking: false,
        isInvalid: false,
        otp: {
          input0: '',
          input1: '',
          input2: '',
          input3: '',
          input4: '',
          input5: '',
        },
        ...(action.payload as Identity),
      };
      state.resendClockData = {
        threshold: 60,
        secondsLeft: 60,
      };
    });

    builder.addCase(sendOtpWithChallengeIdSliceReq.rejected, (state, action: RejectedAction) => {
      state.isLoading = false;
      state.hasError = true;
      state.error = action.error;
    });

    builder.addCase(sendOtpWithChallengeIdSliceReq.pending, (state) => {
      state.isLoading = true;
      state.hasError = false;
      state.error = {};
    });

    /**
     * update password for logged in user
     */
    builder.addCase(updatePasswordIdentitySliceReq.fulfilled, (state) => {
      state.isLoading = false;
      state.hasError = false;
      state.updatePasswordData.isPasswordChanged = true;
    });

    builder.addCase(updatePasswordIdentitySliceReq.rejected, (state) => {
      state.isLoading = false;
      state.hasError = true;
    });

    builder.addCase(updatePasswordIdentitySliceReq.pending, (state) => {
      state.isLoading = true;
    });

    /**
     * Verify Otp
     */
    builder.addCase(verifyOtpIdentitySliceReq.rejected, (state) => {
      state.isLoading = false;
      state.hasError = false;
      state.otpData = {
        ...state.otpData,
        isVerified: false,
        isChecking: false,
        isInvalid: true,
      };
    });

    builder.addCase(verifyOtpIdentitySliceReq.pending, (state) => {
      state.isLoading = false;
      state.hasError = false;
      state.otpData = {
        ...state.otpData,
        isVerified: false,
        isChecking: true,
        isInvalid: false,
      };
    });

    builder.addCase(verifyOtpIdentitySliceReq.fulfilled, (state, action: FulfilledAction) => {
      state.isLoading = false;
      state.hasError = false;
      state.otpData = {
        ...state.otpData,
        isVerified: true,
        isChecking: false,
        isInvalid: false,
        ...(action.payload as Identity),
      };
    });

    /**
     * Resend Otp
     */
    builder.addCase(resendOtpIdentitySliceReq.fulfilled, (state, action: FulfilledAction) => {
      state.isLoading = false;
      state.hasError = false;
      state.sharedData = {
        ...state.sharedData,
        ...(action.payload as Identity),
      };
    });

    builder.addCase(forgotAmwayIdIdentitySliceReq.fulfilled, (state) => {
      state.isLoading = false;
      state.hasError = false;
      state.forgotUsername = {
        ...state.forgotUsername,
        isUsernameSent: true,
        showHelpModal: false,
      };
    });

    /**
     * Generate Token
     */
    builder.addCase(generateTokenIdentitySliceReq.rejected, (state) => {
      state.isLoading = false;
      state.hasError = true;
    });

    /**
     * Generate Otp
     */
    builder.addCase(generateOtpIdentitySliceReq.fulfilled, (state, action: FulfilledAction) => {
      state.isLoading = false;
      state.hasError = false;
      state.otpData = {
        ...state.otpData,
        ...(action.payload as Identity),
        isOtpSent: true,
      };
      state.userData = {
        ...state.userData,
      };
    });

    builder.addCase(generateOtpIdentitySliceReq.rejected, (state) => {
      state.isLoading = false;
      state.hasError = true;
      state.sendOtpData = { ...state.sendOtpData, hasError: true };
    });
    /**
     * Create Password with workflow ID
     */
    builder.addCase(createPasswordWithWorkflowIdentitySliceReq.fulfilled, (state, action: FulfilledAction) => {
      state.isLoading = false;
      state.hasError = false;
      state.forgotPasswordPageData = {
        ...state.forgotPasswordPageData,
      };
      const { workflowId, challengeId } = action.payload as Identity;
      state.forgotPasswordPageData.workflowId = workflowId;
      state.forgotPasswordPageData.challengeId = challengeId;
    });
    builder.addCase(createPasswordWithWorkflowIdentitySliceReq.rejected, (state) => {
      state.isLoading = false;
      state.hasError = true;
      state.forgotPasswordPageData = {
        ...state.forgotPasswordPageData,
      };
      state.forgotPasswordPageData.hasError = true;
    });
    /**
     * Verify otp with challenge ID
     */
    builder.addCase(verifyOtpWithChallengeIdSliceReq.rejected, (state) => {
      state.isLoading = false;
      state.hasError = false;
      state.otpData = {
        ...state.otpData,
        isVerified: false,
        isChecking: false,
        isInvalid: true,
      };
    });

    builder.addCase(verifyOtpWithChallengeIdSliceReq.pending, (state) => {
      state.isLoading = false;
      state.hasError = false;
      state.otpData = {
        ...state.otpData,
        isVerified: false,
        isChecking: true,
        isInvalid: false,
      };
    });

    builder.addCase(verifyOtpWithChallengeIdSliceReq.fulfilled, (state, action: FulfilledAction) => {
      state.isLoading = false;
      state.hasError = false;
      state.otpData = {
        ...state.otpData,
        isVerified: true,
        isChecking: false,
        isInvalid: false,
        ...(action.payload as Identity),
      };
    });
    /**
     * Common reducers
     */
    builder.addMatcher(isRejectedAction, (state: InitialIdentityState) => {
      state.isLoading = false;
    });
    builder.addMatcher(isFulfilledAction, (state: InitialIdentityState) => {
      state.isLoading = false;
    });
    builder.addMatcher(isPendingGenericAction, (state: InitialIdentityState) => {
      state.isLoading = true;
    });
  },
  initialState,
  name: 'commerceIdentity',
  reducers: {
    resetIdentity: () => initialState,
    incrementOtpRequestCount(state) {
      state.otpRequestCount += 1;
    },
    updateStateForUpdatePassword: (state, action) => {
      state.updatePasswordData = {
        ...state.updatePasswordData,
        ...action.payload,
      };
    },
    updateSendOtpData: (state, action) => {
      state.sendOtpData = { ...state.sendOtpData, ...action.payload };
    },
    updateUserData: (state, action) => {
      state.userData = { ...state.userData, ...action.payload };
    },
    updateUsernameData: (state, action) => {
      state.usernameData = { ...state.usernameData, ...action.payload };
    },
    updateOtpData: (state, action) => {
      state.otpData = { ...state.otpData, ...action.payload };
    },
    updateForgotUsernameData: (state, action) => {
      state.forgotUsername = { ...state.forgotUsername, ...action.payload };
    },
    updateResendClockData: (state, action) => {
      state.resendClockData = { ...state.resendClockData, ...action.payload };
    },
    updateForgotPasswordData: (state, action) => {
      state.forgotPassword = {
        ...state.forgotPassword,
        ...action.payload,
      };
    },
    updateAmwayForgotPasswordPageData: (state, action) => {
      state.forgotPasswordPageData = {
        ...state.forgotPasswordPageData,
        ...action.payload,
      };
    },
    updateLoginPageData: (state, action) => {
      state.loginPageData = {
        ...state.loginPageData,
        ...action.payload,
      };
    },
    updateUpdatePasswordData: (state, action) => {
      state.updatePasswordData = {
        ...state.updatePasswordData,
        ...action.payload,
      };
    },
  },
});

export const {
  resetIdentity,
  updateStateForUpdatePassword,
  incrementOtpRequestCount,
  updateSendOtpData,
  updateUserData,
  updateUsernameData,
  updateOtpData,
  updateResendClockData,
  updateForgotUsernameData,
  updateForgotPasswordData,
  updateAmwayForgotPasswordPageData,
  updateLoginPageData,
  updateUpdatePasswordData,
} = identitySlice.actions;
